// manufacturing/WasteEntry.jsx - Complete fixed version
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Edit, Trash2, Printer, ChevronUp,
    AlertTriangle, Calendar, Package, DollarSign, AlertCircle
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const WasteEntry = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [wasteEntries, setWasteEntries] = useState([]);
    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedWaste, setSelectedWaste] = useState(null);
    const [editWasteProductionId, setEditWasteProductionId] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        waste_date: new Date().toISOString().split('T')[0],
        production_id: '',
        notes: '',
        items: []
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [selectedProduction, setSelectedProduction] = useState(null);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        waste_type: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Waste type options
    const wasteTypeOptions = [
        { value: 'raw_material', label: 'Raw Material Waste' },
        { value: 'finished_good', label: 'Finished Good Waste' }
    ];

    // Check permissions
    const canCreate = true;

    // Load waste entries
    const loadWasteEntries = useCallback(async (page = pagination.page) => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                waste_type: filters.waste_type,
                from_date: filters.from_date,
                to_date: filters.to_date
            };

            const result = await manufacturingActions.getWasteEntries(params);

            if (result.success) {
                setWasteEntries(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                toast.error(result.message || 'Failed to load waste entries');
            }
        } catch (error) {
            console.error('Error loading waste entries:', error);
            toast.error('Failed to load waste entries');
        } finally {
            setLoading(false);
        }
    }, [pagination.limit, pagination.page, filters.search, filters.waste_type, filters.from_date, filters.to_date]);

    // Load productions for dropdown - FIXED
    const loadProductions = useCallback(async () => {
        try {
            const result = await manufacturingActions.getProductions({ status: 'completed', limit: 1000 });
            if (result.success) {
                setProductions(result.data || []);
                console.log('Productions loaded:', result.data.length);
            }
        } catch (error) {
            console.error('Error loading productions:', error);
        }
    }, []);



    // Initial load
    useEffect(() => {
        loadWasteEntries();
        loadProductions();
    }, [loadWasteEntries, loadProductions]);

    // Handle production selection - FIXED
    const handleProductionSelect = async (selected) => {
        if (!selected) {
            setSelectedProduction(null);
            setFormData(prev => ({
                ...prev,
                production_id: '',
                items: []
            }));
            return;
        }

        try {
            setLoading(true);
            const result = await manufacturingActions.getProduction(selected.value);

            if (result.success) {
                const production = result.data;
                setSelectedProduction(production);

                // Build items array from raw materials and finished goods
                const items = [];

                // Add raw materials from production
                if (production.raw_materials && Array.isArray(production.raw_materials)) {
                    production.raw_materials.forEach((item, idx) => {
                        items.push({
                            id: Date.now() + idx,
                            product_id: item.product_id,
                            product_name: item.product_name,
                            sku: item.sku,
                            waste_quantity: '',
                            unit: item.unit || 'PCS',
                            waste_type: 'raw_material',
                            reason: '',
                            wastage_cost: 0,
                            unit_cost: item.unit_cost || 0,
                            max_quantity: item.quantity_used || 0,
                            remarks: ''
                        });
                    });
                }

                // Add finished goods from production
                if (production.finished_goods && Array.isArray(production.finished_goods)) {
                    production.finished_goods.forEach((item, idx) => {
                        items.push({
                            id: Date.now() + idx + 1000,
                            product_id: item.product_id,
                            product_name: item.product_name,
                            sku: item.sku,
                            waste_quantity: '',
                            unit: item.unit || 'PCS',
                            waste_type: 'finished_good',
                            reason: '',
                            wastage_cost: 0,
                            unit_cost: item.unit_cost || 0,
                            max_quantity: item.quantity_produced || 0,
                            remarks: ''
                        });
                    });
                }

                console.log('Loaded items for production:', items);

                setFormData(prev => ({
                    ...prev,
                    production_id: production.id,
                    items: items
                }));
            } else {
                toast.error(result.message || 'Failed to load production details');
            }
        } catch (error) {
            console.error('Error loading production:', error);
            toast.error('Failed to load production details');
        } finally {
            setLoading(false);
        }
    };

    // Handle waste quantity change
    const handleWasteQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        const maxQty = updatedItems[index].max_quantity;

        if (qty > maxQty) {
            toast.warning(`Waste quantity cannot exceed available quantity (${maxQty})`);
            updatedItems[index].waste_quantity = String(maxQty); // store as string
        } else {
            updatedItems[index].waste_quantity = quantity; // keep raw string so user can type 0.098
        }

        updatedItems[index].wastage_cost = (updatedItems[index].unit_cost || 0) * (parseFloat(updatedItems[index].waste_quantity) || 0);
        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (errors[`waste_qty_${index}`]) {
            setErrors(prev => ({ ...prev, [`waste_qty_${index}`]: '' }));
        }
    };

    // Handle reason change
    const handleReasonChange = (index, reason) => {
        const updatedItems = [...formData.items];
        updatedItems[index].reason = reason;
        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (errors[`reason_${index}`]) {
            setErrors(prev => ({ ...prev, [`reason_${index}`]: '' }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.production_id) {
            newErrors.production_id = 'Please select a production';
        }

        let hasWaste = false;
        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            if (parseFloat(item.waste_quantity) > 0) {
                hasWaste = true;
                if (!item.reason) {
                    newErrors[`reason_${i}`] = 'Reason is required for waste';
                }
            }
        }

        if (!hasWaste) {
            newErrors.items = 'At least one waste item must be recorded';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission - FIXED
    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setSubmitting(true);

        const wasteItems = formData.items
            .filter(item => parseFloat(item.waste_quantity) > 0)
            .map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                sku: item.sku,
                waste_quantity: parseFloat(item.waste_quantity) || 0,
                unit: item.unit,
                waste_type: item.waste_type,
                reason: item.reason,
                wastage_cost: item.wastage_cost,
                unit_cost: item.unit_cost,
                remarks: item.remarks || ''
            }));

        const dataToSend = {
            waste_date: formData.waste_date,
            production_id: formData.production_id,
            notes: formData.notes,
            items: wasteItems
        };

        console.log('Submitting waste data:', dataToSend);

        try {
            const result = await manufacturingActions.createWasteEntry(dataToSend);

            if (result.success) {
                toast.success(result.message || 'Waste entry created successfully');
                resetForm();
                setShowCreateModal(false);
                loadWasteEntries();
                loadProductions();
            } else {
                toast.error(result.message || 'Failed to create waste entry');
            }
        } catch (error) {
            console.error('Error creating waste entry:', error);
            toast.error(error.message || 'Failed to create waste entry');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            waste_date: new Date().toISOString().split('T')[0],
            production_id: '',
            notes: '',
            items: []
        });
        setSelectedProduction(null);
        setErrors({});
    };

    // Edit waste entry
    const handleEditWaste = async (waste) => {
        setLoading(true);
        try {
            const wasteResult = await manufacturingActions.getWasteEntry(waste.production_id);
            if (!wasteResult.success) {
                toast.error(wasteResult.message || 'Failed to load waste details');
                return;
            }
            const wasteData = wasteResult.data;

            // Build items from existing waste records
            const items = (wasteData.items || []).map((item, idx) => ({
                id: item.id || Date.now() + idx,
                product_id: item.product_id,
                product_name: item.product_name,
                sku: item.sku,
                waste_quantity: String(parseFloat(item.waste_quantity) || 0),
                unit: item.unit || 'PCS',
                waste_type: item.waste_type || 'raw_material',
                reason: item.reason || '',
                wastage_cost: parseFloat(item.wastage_cost) || 0,
                unit_cost: parseFloat(item.unit_cost) || 0,
                max_quantity: 9999, // no hard cap in edit mode
                remarks: item.remarks || ''
            }));

            setFormData({
                waste_date: wasteData.waste_date || new Date().toISOString().split('T')[0],
                production_id: waste.production_id,
                notes: wasteData.notes || '',
                items
            });
            setEditWasteProductionId(waste.production_id);
            const prod = productions.find(p => p.id === waste.production_id);
            setSelectedProduction(prod || { production_no: waste.production_no, batch_no: waste.batch_no, id: waste.production_id });
            setErrors({});
            setShowEditModal(true);
        } catch (error) {
            console.error('Error loading waste for edit:', error);
            toast.error('Failed to load waste entry for editing');
        } finally {
            setLoading(false);
        }
    };

    // Submit update
    const handleUpdateWaste = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }
        setSubmitting(true);
        const wasteItems = formData.items
            .filter(item => parseFloat(item.waste_quantity) > 0)
            .map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                sku: item.sku,
                waste_quantity: parseFloat(item.waste_quantity) || 0,
                unit: item.unit,
                waste_type: item.waste_type,
                reason: item.reason,
                wastage_cost: item.wastage_cost,
                unit_cost: item.unit_cost,
                remarks: item.remarks || ''
            }));

        try {
            const result = await manufacturingActions.updateWasteEntry(editWasteProductionId, {
                waste_date: formData.waste_date,
                production_id: editWasteProductionId,
                notes: formData.notes,
                items: wasteItems
            });
            if (result.success) {
                toast.success(result.message || 'Waste entry updated successfully');
                setShowEditModal(false);
                resetForm();
                loadWasteEntries();
            } else {
                toast.error(result.message || 'Failed to update waste entry');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update waste entry');
        } finally {
            setSubmitting(false);
        }
    };

    // Delete waste entry
    const handleDeleteWaste = async (waste) => {
        const confirm = await MySwal.fire({
            title: 'Delete Waste Entry?',
            html: `<p>Delete all waste records for <strong>${waste.production_no}</strong> (${waste.batch_no})?<br/>This will restore any deducted finished goods stock.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete'
        });
        if (!confirm.isConfirmed) return;
        try {
            const result = await manufacturingActions.deleteWasteEntry(waste.production_id);
            if (result.success) {
                toast.success(result.message || 'Waste entry deleted');
                loadWasteEntries();
            } else {
                toast.error(result.message || 'Failed to delete waste entry');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to delete waste entry');
        }
    };

    // View waste details - FIXED with better debugging
    const handleViewWaste = async (waste) => {
        try {
            setLoading(true);
            console.log('Viewing waste:', waste);
            console.log('Production ID being requested:', waste.production_id);

            // Use production_id to fetch details
            const result = await manufacturingActions.getWasteEntry(waste.production_id);
            console.log('API Full Response:', result);
            console.log('API Response Data:', result.data);
            console.log('Items in response:', result.data?.items);

            if (result.success && result.data) {
                setSelectedWaste(result.data);
                setShowViewModal(true);

                // Show toast with item count
                if (result.data.items && result.data.items.length > 0) {
                    toast.success(`Loaded ${result.data.items.length} waste item(s)`);
                } else {
                    toast.warning('No waste items found in the response');
                }
            } else {
                toast.error(result.message || 'Failed to load waste details');
                // Fallback: use the data from list
                setSelectedWaste(waste);
                setShowViewModal(true);
            }
        } catch (error) {
            console.error('Error viewing waste:', error);
            toast.error('Failed to load detailed waste information');
            // Fallback: show list data
            setSelectedWaste(waste);
            setShowViewModal(true);
        } finally {
            setLoading(false);
        }
    };
    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get waste type badge
    const getWasteTypeBadge = (type) => {
        if (type === 'raw_material') {
            return <span className="badge bg-info">Raw Material</span>;
        } else if (type === 'finished_good') {
            return <span className="badge bg-warning">Finished Good</span>;
        } else if (type === 'both') {
            return <span className="badge bg-secondary">Both</span>;
        }
        return <span className="badge bg-secondary">Unknown</span>;
    };

    // Production options for dropdown - FIXED formatting
    const productionOptions = productions.map(prod => ({
        value: prod.id,
        label: `${prod.production_no || prod.id} - ${prod.batch_no} (${formatDate(prod.production_date)})`
    }));

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Calculate totals
    const totals = {
        totalWaste: wasteEntries.reduce((sum, w) => sum + (parseFloat(w.total_quantity) || 0), 0),
        totalCost: wasteEntries.reduce((sum, w) => sum + (parseFloat(w.total_cost) || 0), 0),
        rawMaterialWaste: wasteEntries.filter(w => w.waste_type === 'raw_material').length,
        finishedGoodWaste: wasteEntries.filter(w => w.waste_type === 'finished_good').length
    };

    // Calculate form totals
    const formTotals = {
        totalQuantity: formData.items.reduce((sum, item) => sum + (parseFloat(item.waste_quantity) || 0), 0),
        totalCost: formData.items.reduce((sum, item) => sum + (parseFloat(item.wastage_cost) || 0), 0),
        wastePercentage: selectedProduction?.total_quantity > 0
            ? ((formData.items.reduce((sum, item) => sum + (parseFloat(item.waste_quantity) || 0), 0) / selectedProduction.total_quantity) * 100).toFixed(2)
            : 0
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Waste Entry</h4>
                        <h6>Record and track production waste</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'PDF')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Excel')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <Link onClick={() => window.print()}>
                                    <Printer />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => loadWasteEntries()}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />
                                Record Waste
                            </button>
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                                        <AlertTriangle size={24} className="text-danger" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Waste Entries</p>
                                        <h3 className="mb-0">{pagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Waste Quantity</p>
                                        <h3 className="mb-0">{totals.totalWaste.toLocaleString()}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                        <DollarSign size={24} className="text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Waste Cost</p>
                                        <h3 className="mb-0">{formatCurrency(totals.totalCost)}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                        <AlertCircle size={24} className="text-success" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Avg Waste per Production</p>
                                        <h3 className="mb-0">
                                            {productions.length > 0
                                                ? (totals.totalWaste / productions.length).toFixed(2)
                                                : 0}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Production no, batch no, product..."
                                            value={filters.search}
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            onKeyPress={(e) => e.key === 'Enter' && loadWasteEntries(1)}
                                        />
                                        <button className="btn btn-primary" onClick={() => loadWasteEntries(1)}>
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Waste Type</label>
                                    <Select
                                        options={wasteTypeOptions}
                                        isClearable
                                        placeholder="All Types"
                                        onChange={(selected) => {
                                            setFilters(prev => ({ ...prev, waste_type: selected?.value || '' }));
                                            loadWasteEntries(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => {
                                            setFilters(prev => ({ ...prev, from_date: e.target.value }));
                                            loadWasteEntries(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => {
                                            setFilters(prev => ({ ...prev, to_date: e.target.value }));
                                            loadWasteEntries(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-1">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100" onClick={() => {
                                        setFilters({ search: '', waste_type: '', from_date: '', to_date: '' });
                                        loadWasteEntries(1);
                                    }}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Waste Entries Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : wasteEntries.length === 0 ? (
                            <div className="text-center py-5">
                                <AlertTriangle size={48} className="text-muted mb-3" />
                                <h5>No waste entries found</h5>
                                <p className="text-muted">Record waste to track production losses</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />
                                        Record Waste
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedRows.length === wasteEntries.length && wasteEntries.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRows(wasteEntries.map(w => w.waste_no || w.id));
                                                        } else {
                                                            setSelectedRows([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>Waste No</th>
                                            <th>Date</th>
                                            <th>Production No</th>
                                            <th>Batch No</th>
                                            <th>Waste Type</th>
                                            <th>Total Qty</th>
                                            <th>Total Cost</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wasteEntries.map((waste) => (
                                            <tr key={waste.waste_no || waste.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.includes(waste.waste_no || waste.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, waste.waste_no || waste.id]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== (waste.waste_no || waste.id)));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{waste.waste_no}</strong>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Calendar size={14} className="me-1 text-muted" />
                                                        {formatDate(waste.waste_date)}
                                                    </div>
                                                </td>
                                                <td>{waste.production_no}</td>
                                                <td><code>{waste.batch_no}</code></td>
                                                <td>{getWasteTypeBadge(waste.waste_type)}</td>
                                                <td>{parseFloat(waste.total_quantity).toLocaleString()}</td>
                                                <td className="text-danger fw-bold">{formatCurrency(waste.total_cost)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info me-1"
                                                        onClick={() => handleViewWaste(waste)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-warning me-1"
                                                        onClick={() => handleEditWaste(waste)}
                                                        title="Edit"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteWaste(waste)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadWasteEntries(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadWasteEntries(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadWasteEntries(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Waste Entry Modal - FIXED */}
            <Modal show={showCreateModal} onHide={() => { resetForm(); setShowCreateModal(false); }} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <AlertTriangle size={18} className="me-2" />
                        Record Waste
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row mb-4">
                        <div className="col-md-4">
                            <label className="form-label">Waste Date *</label>
                            <input
                                type="date"
                                className="form-control"
                                value={formData.waste_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, waste_date: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">Select Production *</label>
                            <Select
                                options={productionOptions}
                                onChange={handleProductionSelect}
                                placeholder="Choose production to record waste..."
                                isDisabled={submitting}
                                className={errors.production_id ? 'is-invalid' : ''}
                                value={productionOptions.find(opt => opt.value === formData.production_id)}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                            {errors.production_id && <div className="text-danger small mt-1">{errors.production_id}</div>}
                        </div>
                        <div className="col-12 mt-3">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-control"
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                rows="2"
                                placeholder="Additional notes about waste"
                            />
                        </div>
                    </div>

                    {selectedProduction && (
                        <div className="border-top pt-3">
                            <h6 className="fw-bold mb-3">
                                <AlertCircle size={16} className="me-2 text-warning" />
                                Record Waste for Production: {selectedProduction.production_no} - {selectedProduction.batch_no}
                            </h6>

                            {errors.items && <div className="alert alert-danger">{errors.items}</div>}

                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '25%' }}>Product</th>
                                            <th style={{ width: '10%' }}>SKU</th>
                                            <th style={{ width: '10%' }}>Available Qty</th>
                                            <th style={{ width: '12%' }}>Waste Quantity *</th>
                                            <th style={{ width: '8%' }}>Unit</th>
                                            <th style={{ width: '20%' }}>Reason *</th>
                                            <th style={{ width: '10%' }}>Cost</th>
                                            <th style={{ width: '5%' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.items.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted">
                                                    Loading production items...
                                                </td>
                                            </tr>
                                        ) : (
                                            formData.items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <strong>{item.product_name}</strong>
                                                        <small className="d-block text-muted">{item.waste_type === 'raw_material' ? 'Raw Material' : 'Finished Good'}</small>
                                                    </td>
                                                    <td><code>{item.sku}</code></td>
                                                    <td className="fw-bold text-primary">{parseFloat(item.max_quantity).toLocaleString()} {item.unit}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className={`form-control form-control-sm ${errors[`waste_qty_${index}`] ? 'is-invalid' : ''}`}
                                                            value={item.waste_quantity || ''}
                                                            onChange={(e) => handleWasteQuantityChange(index, e.target.value)}

                                                            min="0"
                                                            max={item.max_quantity}
                                                            placeholder="Enter waste qty"
                                                        />
                                                    </td>
                                                    <td>{item.unit}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className={`form-control form-control-sm ${errors[`reason_${index}`] ? 'is-invalid' : ''}`}
                                                            value={item.reason || ''}
                                                            onChange={(e) => handleReasonChange(index, e.target.value)}
                                                            placeholder="Reason for waste"
                                                            disabled={!parseFloat(item.waste_quantity)}
                                                        />
                                                        {errors[`reason_${index}`] && <div className="text-danger small mt-1">{errors[`reason_${index}`]}</div>}
                                                    </td>
                                                    <td className="text-danger">
                                                        {formatCurrency(item.wastage_cost)}
                                                    </td>
                                                    <td className="text-center">
                                                        {parseFloat(item.waste_quantity) > 0 && (
                                                            <span className="badge bg-warning">Recorded</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                    {formData.items.some(item => parseFloat(item.waste_quantity) > 0) && (
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="3" className="text-end fw-bold">Totals:</td>
                                                <td className="fw-bold">{formTotals.totalQuantity.toFixed(2)} units</td>
                                                <td></td>
                                                <td></td>
                                                <td className="fw-bold text-danger">{formatCurrency(formTotals.totalCost)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>

                            {/* Waste Summary */}
                            {formData.items.some(item => parseFloat(item.waste_quantity) > 0) && (
                                <div className="alert alert-warning mt-3">
                                    <h6 className="mb-2">Waste Summary</h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <strong>Total Waste Quantity:</strong> {formTotals.totalQuantity.toFixed(2)} units
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Total Waste Cost:</strong> {formatCurrency(formTotals.totalCost)}
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Waste Percentage:</strong> {formTotals.wastePercentage}%
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { resetForm(); setShowCreateModal(false); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleSubmit}
                        disabled={submitting || !selectedProduction}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <AlertTriangle size={16} className="me-2" />
                                Record Waste
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Waste Entry Modal */}
            <Modal show={showEditModal} onHide={() => { resetForm(); setShowEditModal(false); }} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Edit size={18} className="me-2" />
                        Edit Waste Entry
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row mb-4">
                        <div className="col-md-4">
                            <label className="form-label">Waste Date *</label>
                            <input
                                type="date"
                                className="form-control"
                                value={formData.waste_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, waste_date: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">Production</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={selectedProduction
                                    ? `${selectedProduction.production_no} — Batch: ${selectedProduction.batch_no}`
                                    : `Production #${editWasteProductionId}`}
                                disabled
                                readOnly
                            />
                            <small className="text-muted">Production cannot be changed when editing waste</small>
                        </div>
                        <div className="col-12 mt-3">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-control"
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                rows="2"
                                placeholder="Additional notes about waste"
                            />
                        </div>
                    </div>

                    <div className="border-top pt-3">
                        <h6 className="fw-bold mb-3">
                            <AlertCircle size={16} className="me-2 text-warning" />
                            Waste Items
                        </h6>

                        {errors.items && <div className="alert alert-danger">{errors.items}</div>}

                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '25%' }}>Product</th>
                                        <th style={{ width: '10%' }}>SKU</th>
                                        <th style={{ width: '12%' }}>Waste Quantity *</th>
                                        <th style={{ width: '8%' }}>Unit</th>
                                        <th style={{ width: '25%' }}>Reason *</th>
                                        <th style={{ width: '10%' }}>Cost</th>
                                        <th style={{ width: '10%' }}>Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">
                                                No waste items loaded...
                                            </td>
                                        </tr>
                                    ) : (
                                        formData.items.map((item, index) => (
                                            <tr key={item.id || index}>
                                                <td>
                                                    <strong>{item.product_name}</strong>
                                                </td>
                                                <td><code>{item.sku || 'N/A'}</code></td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`form-control form-control-sm ${errors[`waste_qty_${index}`] ? 'is-invalid' : ''}`}
                                                        value={item.waste_quantity || ''}
                                                        onChange={(e) => handleWasteQuantityChange(index, e.target.value)}
                                                        min="0"
                                                        placeholder="Enter waste qty"
                                                    />
                                                    {errors[`waste_qty_${index}`] && <div className="text-danger small mt-1">{errors[`waste_qty_${index}`]}</div>}
                                                </td>
                                                <td>{item.unit}</td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className={`form-control form-control-sm ${errors[`reason_${index}`] ? 'is-invalid' : ''}`}
                                                        value={item.reason || ''}
                                                        onChange={(e) => handleReasonChange(index, e.target.value)}
                                                        placeholder="Reason for waste"
                                                    />
                                                    {errors[`reason_${index}`] && <div className="text-danger small mt-1">{errors[`reason_${index}`]}</div>}
                                                </td>
                                                <td className="text-danger">
                                                    {formatCurrency(item.wastage_cost)}
                                                </td>
                                                <td>
                                                    {item.waste_type === 'raw_material'
                                                        ? <span className="badge bg-info">Raw Material</span>
                                                        : <span className="badge bg-warning">Finished Good</span>
                                                    }
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {formData.items.some(item => parseFloat(item.waste_quantity) > 0) && (
                                    <tfoot className="table-light">
                                        <tr>
                                            <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                            <td className="fw-bold">{formTotals.totalQuantity.toFixed(2)} units</td>
                                            <td></td>
                                            <td></td>
                                            <td className="fw-bold text-danger">{formatCurrency(formTotals.totalCost)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>

                        {formData.items.some(item => parseFloat(item.waste_quantity) > 0) && (
                            <div className="alert alert-warning mt-3">
                                <h6 className="mb-2">Waste Summary</h6>
                                <div className="row">
                                    <div className="col-md-4">
                                        <strong>Total Waste Quantity:</strong> {formTotals.totalQuantity.toFixed(2)} units
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Total Waste Cost:</strong> {formatCurrency(formTotals.totalCost)}
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Waste Percentage:</strong> {formTotals.wastePercentage}%
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { resetForm(); setShowEditModal(false); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleUpdateWaste}
                        disabled={submitting || formData.items.length === 0}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Updating...
                            </>
                        ) : (
                            <>
                                <Edit size={16} className="me-2" />
                                Update Waste
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Waste Modal - FIXED */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <AlertTriangle size={18} className="me-2" />
                        Waste Details - {selectedWaste?.waste_no}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedWaste && (
                        <>
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="mb-3">Waste Information</h6>
                                            <p><strong>Waste No:</strong> {selectedWaste.waste_no}</p>
                                            <p><strong>Date:</strong> {formatDate(selectedWaste.waste_date)}</p>
                                            <p><strong>Production No:</strong> {selectedWaste.production_no}</p>
                                            <p><strong>Batch No:</strong> <code>{selectedWaste.batch_no}</code></p>
                                            {selectedWaste.notes && <p><strong>Notes:</strong> {selectedWaste.notes}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="mb-3">Waste Summary</h6>
                                            <p><strong>Total Waste Quantity:</strong> {parseFloat(selectedWaste.total_quantity || 0).toLocaleString()} units</p>
                                            <p><strong>Total Waste Cost:</strong> {formatCurrency(selectedWaste.total_cost)}</p>
                                            <p><strong>Raw Material Waste:</strong> {parseFloat(selectedWaste.raw_material_waste || 0).toLocaleString()} units</p>
                                            <p><strong>Finished Good Waste:</strong> {parseFloat(selectedWaste.finished_good_waste || 0).toLocaleString()} units</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h6 className="fw-bold mb-3">Waste Items</h6>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Quantity</th>
                                            <th>Unit</th>
                                            <th>Type</th>
                                            <th>Reason</th>
                                            <th>Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedWaste.items && selectedWaste.items.length > 0 ? (
                                            selectedWaste.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <strong>{item.product_name}</strong>
                                                        {item.remarks && <small className="d-block text-muted">{item.remarks}</small>}
                                                    </td>
                                                    <td><code>{item.sku || 'N/A'}</code></td>
                                                    <td className="text-danger fw-bold">
                                                        {parseFloat(item.waste_quantity || 0).toLocaleString()} {item.unit}
                                                    </td>
                                                    <td>{item.unit || 'PCS'}</td>
                                                    <td>
                                                        {item.waste_type === 'raw_material' ?
                                                            <span className="badge bg-info">Raw Material</span> :
                                                            <span className="badge bg-warning">Finished Good</span>
                                                        }
                                                    </td>
                                                    <td>{item.reason || '—'}</td>
                                                    <td className="text-danger fw-bold">{formatCurrency(item.wastage_cost || 0)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <AlertCircle size={24} className="text-muted mb-2" />
                                                    <p className="text-muted mb-0">No waste items details available</p>
                                                    <small className="text-muted">Debug: Items array is {selectedWaste.items ? 'empty' : 'missing'}</small>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {selectedWaste.items && selectedWaste.items.length > 0 && (
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="6" className="text-end fw-bold">Total Waste Cost:</td>
                                                <td className="fw-bold text-danger">{formatCurrency(selectedWaste.total_cost)}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WasteEntry;